/**
 * useOpenReviewCount — the open-flag count for the "Needs review" rail badge
 * (BON-381).
 *
 * Reads `GET /api/v2/payment-reviews/count`, which exists so the badge never
 * pays for the whole queue payload. Three deliberate properties, because this
 * runs in the app chrome on every authenticated page:
 *   - it does not fetch at all unless the viewer holds the read permission, so
 *     a teacher never generates a 403;
 *   - a failure is swallowed and leaves the count undefined — the badge simply
 *     doesn't render. A count endpoint must never be able to break the rail;
 *   - `undefined` (not 0) while unknown, so `NavRailItem.badge` stays hidden
 *     rather than flashing a zero.
 *
 * It also REFETCHES when a transition commits below this chrome — see
 * `reviewCountSignal`. Without that, resolving the last flagged item left the
 * badge advertising work that no longer existed until a full page reload.
 */
import { useEffect, useState } from 'react';

import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/permissions';

import { fetchOpenReviewCount } from './reviewApi';
import { onReviewCountStale } from './reviewCountSignal';

export function useOpenReviewCount(): number | undefined {
  const { hasAnyPermission, loading } = usePermissions();
  const allowed = !loading && hasAnyPermission(Permission.READ_BOOKINGS, Permission.READ_ALL);
  const [count, setCount] = useState<number | undefined>(undefined);
  // Bumped when a resolve or reopen commits below this chrome (U14).
  const [staleToken, setStaleToken] = useState(0);

  useEffect(() => onReviewCountStale(() => setStaleToken((t) => t + 1)), []);

  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    fetchOpenReviewCount(controller.signal)
      .then((n) => {
        if (!controller.signal.aborted) setCount(n);
      })
      .catch(() => {
        // Deliberately silent: see the header note.
      });
    return () => controller.abort();
  }, [allowed, staleToken]);

  return count;
}
